import router from "@/router";
import axios, { type AxiosError } from "axios";
import type { App } from "vue";
import { ApiErrorCode, ApiResultCode } from "@/types/Enums";
import { useLoginStore } from "@/app/store/LoginStore";
import { getCookie } from "@/app/plugins/global";
/**
 * Axios 응답코드가 ApiResultCode 중 하나인지 판별
 *
 * @param value
 */
const isSuccess = (value: string): value is ApiResultCode => {
  return Object.values(ApiResultCode).includes(value as ApiResultCode);
};

/**
 * Axios 응답코드가 ApiErrorCode 중 하나인지 판별
 *
 * @param value
 */
const isError = (value: string): value is ApiErrorCode => {
  return Object.values(ApiErrorCode).includes(value as ApiErrorCode);
};

/**
 * axios 기본설정 및 request/response 인터셉터 등록
 *
 * @remarks
 * - Response : 응답의 `code`/`message`를 기반으로 전역 alert/toast 호출 또는 데이터 반환
 * - Error: 네트워크/서버 에러 시 toast 표시 및 500 에러일 경우 `/noServer`로 라우팅
 * @see {@link ApiResultCode}
 * @see {@link ApiErrorCode}
 */
export const setAxios = {
  install(app: App) {
    axios.defaults.headers.common["content-Type"] = "application/json";
    axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*";
    axios.defaults.withCredentials = true;

    // Request
    axios.interceptors.request.use(
      async (config) => {
        config.headers["Accept-Language"] = sessionStorage.getItem("lang");
        const csrf = getCookie("CSRF-TOKEN");
        if (csrf) {
          config.headers["X-CSRF-TOKEN"] = csrf;
        }

        if (config.method === "get" && config.params) {
          Object.keys(config.params).forEach((key) => {
            const value = config.params[key];
            if (value === null || value === undefined) {
              delete config.params[key];
            }
          });
          config.params = new URLSearchParams(config.params);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response
    axios.interceptors.response.use(
      async (res) => {
        res.headers["Access-Control-Allow-Origin"] = "*";
        res.headers["Accept-Language"] = navigator.language || navigator.languages[0];
        if (res.config.responseType === "blob") return res;

        // rtn codes
        const code = res.data.code;
        const message = res.data.message;

        console.info(
          "[method] " +
          res.config.method +
          "\n[url] " +
          res.config.url +
          "\n[code] " +
          code +
          "\n[message] " +
          message
        );

        if (isSuccess(code)) {
          switch (code) {
            /** ===============================
             *  성공 코드
             * =============================== */
            case ApiResultCode.API_RES_GET: // 0001 조회 성공
            case ApiResultCode.API_RES_IMAGE: // 0005 이미지 저장 성공
              return await res.data.data;

            case ApiResultCode.API_RES_CREATE: // 0002 생성 성공
            case ApiResultCode.API_RES_UPDATE: // 0003 수정 성공
            case ApiResultCode.API_RES_DELETE: // 0004 삭제 성공
              if (!res.config.headers["noAlert"]) {
                app.config.globalProperties.$alert(message, "success");
              }
              return await res.data.data.result;

            case ApiResultCode.API_RES_LOGIN: // 9001 로그인 성공
              app.config.globalProperties.$toast(message, "success");
              return await res.data.data;

            case ApiResultCode.API_RES_LOGOUT: // 9002 로그아웃 성공
              return await res.data.data;
          }
          /** ===============================
           *  오류 코드
           * =============================== */
        } else if (isError(code)) {
          switch (code) {
            case ApiErrorCode.API_RES_AUTZ_ROUTE_ERR: // 4005 라우트 이동 권한 없음
              app.config.globalProperties.$alert(message, "info");
              router.push("/main");
              break;

            case ApiErrorCode.API_RES_AUTZ_EMPTY: // 4010 활성 권한 미부여
            case ApiErrorCode.API_RES_AUTH_ERR: // 4001 로그인 정보 조회 실패
              app.config.globalProperties.$alert(message, "info");
              await useLoginStore().logout();
              break;

            case ApiErrorCode.API_RES_AUTH_FORCE_ERR: // 4002 중복 로그인 강제 종료
            case ApiErrorCode.API_RES_AUTH_BINDING_ERR: // 4003 UA/IP 바인딩 실패
              app.config.globalProperties.$alert(message, "error");
              await useLoginStore().logout();
              break;

            default:
              app.config.globalProperties.$toast(message, "error");
              break;
          }
        } else {
          app.config.globalProperties.$toast(message, "error");
        }
        /*************************** return code global config **************************/
      },
      async (error: AxiosError) => {
        const errorStatus = error.response?.status;
        const errorMessage = error.message;
        app.config.globalProperties.$toast(errorMessage, "error");
        if (errorStatus === 500) {
          await router.push("/noServer");
        }
        throw error;
      }
    );
  },
};
